/**
Author - Sahil Dhingra
Git - https://github.com/sahildhingra-221
**/

import React from "react";
import styled from "styled-components";
import parseHtmlContent from "helpers/parseHtmlContent";
import SafeLink from "components/commons/SafeLink";

import {colors} from "nxtconstants";
import {WealthyEnv} from "helpers";

import {SectionTitle} from "../common";

const promoImage = `url(${WealthyEnv.BROKING_CDN_URL}/product-slider-bg.png)`;

const ProductSlider = ({
  activeProduct,
  productsList,
  setActiveProduct
}) => {
  return (
    <>
      <Section>
        <WealthyProductsSection>
          <TitleWrapper>
            <SectionTitle>
        All financial products on a single platform!
            </SectionTitle>
          </TitleWrapper>
          <ProductSliderWrapper>
            <ProductShowWrapper>
              <ProductLinksWrapper>
                {
                  productsList.map((item, index) => (
                    <ProductLinkButton
                      className={activeProduct === index ? "active" : ""}
                      key={index}
                      onClick={() => setActiveProduct(index)}
                    >
                      {item.productName}
                    </ProductLinkButton>
                  ))
                }
              </ProductLinksWrapper>
              <ProductImageWrapper>
                {
                  productsList.map((item, index) => (
                    <ProductSliderImage
                      alt={item.productName}
                      className={activeProduct === index ? "active" : ""}
                      key={index}
                      src={item.productImage}
                    />
                  ))
                }
              </ProductImageWrapper>
            </ProductShowWrapper>
            <ProductInfoWrapper>
              {
                productsList.map((item, index) => (
                  <ProductTagline
                    className={activeProduct === index ? "active" : ""}
                    key={index}
                  >
                    {item.productTagline}
                  </ProductTagline>
                ))
              }
              {
                productsList.map((item, index) => (
                  <ProductAbout
                    className={activeProduct === index ? "active" : ""}
                    key={index}
                  >
                    {parseHtmlContent(item.productAbout)}
                  </ProductAbout>
                ))
              }
            </ProductInfoWrapper>
          </ProductSliderWrapper>
        </WealthyProductsSection>
      </Section>
      <AboutTerminalSection>
        <TitleWrapper>
          <SectionTitle>
      Trade & invest with TradingView - an advanced charting platform
          </SectionTitle>
        </TitleWrapper>
        <TerminalFeatures>
          <p><strong>Wealthy Broking</strong> has partnered with TradingView to elevate its charting capabilities. As a global leader in investing and trading platforms, TradingView offers comprehensive market data, financial news, and a  <SafeLink
            href="https://www.tradingview.com/economic-calendar/"
            rel="noopener noreferrer"
            target="_blank"
          >real time economic calendar</SafeLink>, ensuring users stay informed about key financial events.</p>
          <ul>
            <li><strong>Comprehensive features for technical analysis:</strong> <span>Not just the indicators you need, but the indicators you must have</span></li>
            <li><strong>Real-time movements on your screen:</strong> <span>Explore 15+ chart types and add up to 16 charts in one tab with custom timeframes and formulae.</span></li>
            <li><strong>Trade, Invest, & Analyse with Ease:</strong> <span>Find your favourite securities across NSE and BSE with advanced charts.</span></li>
          </ul>
        </TerminalFeatures>

      </AboutTerminalSection>

    </>
  );
};

const Section = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const WealthyProductsSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 1200px) {
    margin-left: 2rem;
    margin-right: 2rem;
  }
`;

const TitleWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const ProductSliderWrapper = styled.div`
  max-width: 1000px;
  display: flex;
  align-items: center;
  padding-top: 4.2rem;
  padding-bottom: 13rem;
  overflow: hidden;

  & > div {
    flex: 1;
  }

  & * {
    transition: all 0.15s ease-in-out;
  }

  @media (max-width: 768px) {
    display: block;
    padding-bottom: 8rem;
  }

  @media (max-width: 576px) {
    overflow: hidden;
    padding-bottom: 4rem;
  }
`;

const ProductShowWrapper = styled.div`
  display: flex;
  align-items: center;
  & > div {
    flex: 1;
  }

  @media (max-width: 576px) {
    & > div {
      width: 50%;
    }
  }
`;

const ProductLinksWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
`;

const ProductLinkButton = styled.button`
  background-color: transparent;
  border: 0;
  outline: 0;
  font-size: 2.4rem;
  color: ${colors.btnGray};
  text-align: left;
  padding: 10px 0;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  white-space: pre;

  &::before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    background-image: url(https://i.wlycdn.com/broking/diamond-star-icon.png);
    background-size: contain;
    top: 13px;
    left: 6px;
    transform: translateX(-150%);
    transition: all 0.15s ease-in-out;
  }

  &.active {
    padding-left: 42px;
    color: ${colors.textBlack};
    &::before {
      transform: unset;
    }
  }

  @media (max-width: 576px) {
    white-space: break-spaces;
    font-size: 1.6rem;
    padding: 6px 0;

    &.active {
      padding-left: 30px;
    }

    &::before {
      height: 10px;
      width: 10px;
      top: 8px;
    }
  }
`;

const ProductImageWrapper = styled.div`
  height: 458px;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    height: 350px;
  }

  @media (max-width: 576px) {
    height: 227px;
  }
`;

const ProductSliderImage = styled.img`
  height: calc(100% + 170px);
  object-fit: cover;
  transform: translateY(150%);
  opacity: 0;
  pointer-events: none;
  order: 1;
  transition: all 0.25s ease-in-out;

  &.active {
    transform: unset;
    opacity: 1;
    pointer-events: all;
    position: relative;
    order: 0;
  }

  @media (max-width: 576px) {
    width: 100%;
  }
`;

const ProductInfoWrapper = styled.div`
  max-width: 300px;

  @media (max-width: 768px) {
    padding-top: 3rem;
  }

  @media (max-width: 576px) {
    width: 100%;
    max-width: 100%;
    display: block;
    overflow: hidden;
  }
`;

const ProductTagline = styled.h2`
  color: ${colors.primary};
  font-size: 2.6rem;
  padding-bottom: 1.6rem;
  font-weight: 600;
  transform: translateX(100%);
  position: absolute;
  opacity: 0;
  transition: all 0s ease-in;

  &.active {
    transform: unset;
    position: relative;
    opacity: 1;
    transition: all 0.15s ease-in;
  }

  @media (max-width: 576px) {
    font-size: 1.6rem;
    padding-bottom: 1.2rem;
    transform: translateY(100%);
  }
`;

const ProductAbout = styled.p`
  color: ${colors.gray};
  font-size: 1.6rem;
  transform: translateX(100%);
  position: absolute;
  opacity: 0;
  transition: all 0s ease-in;

  &.active {
    transform: unset;
    position: relative;
    opacity: 1;
    transition: all 0.2s ease-in;
  }

  @media (max-width: 576px) {
    font-size: 1.4rem;
    transform: translateY(100%);
  }
`;

const AboutTerminalSection = styled.div`
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width:768px){
      margin-left: 2rem;
    }
`;

const TerminalFeatures = styled.div`
    font-size: 2rem;
    max-width: 1200px;
    margin: 0 auto;

    ul {
      margin-top: 2rem;
      margin-bottom: 6rem;
      margin-left: 4rem;
      list-style-type: disc;



      @media (max-width: 768px){
        margin:1rem 2rem;
        margin-bottom: 4rem;
      }
    }

    p {
      margin-top: 1rem;

      font-size: 2rem;
      font-weight: lighter;

          @media (max-width:768px){
          font-size: 1.4rem;
       }
        a {
          font-size: 2rem;
          color: rgb(103,37,244);

          @media (max-width:768px){
          font-size: 1.4rem;
       }
       }
    }

     ul li {
       font-size: 2rem;
       margin-bottom: 1rem;

       span {
         font-weight: lighter;
       }

       @media (max-width:768px){
          font-size: 1.4rem;
       }
     }
`;

export default ProductSlider;
